import { useSelector } from 'react-redux';
import { useGetBonusCampaignQuery, useLazyGetCampaignReportQuery } from '../../../../api/bonusCampaign';
import HeaderTextLayout from '../../../../components/shared/HeaderTextLayout';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createDate,
  DATE,
  DateCreater
} from '../../../../constants/variables/dates';
import {
  CampaignType,
  CampaignReportStatus
} from '../../../../constants/menuLists';
import { actions } from '../../../../redux/store/store';
import ReactSelect from 'react-select';
import MultiSelect from '../../../../constants/multiSelectDropdown';
import { useGetMetrosQuery } from '../../../../api/metro';
import { Refresh } from '@mui/icons-material';
import { CSVLink } from 'react-csv';
import { exportMenu } from '../../../../constants/variables/arrays';
import { CircularProgress, Fade, Menu, MenuItem } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { DateRangePicker } from 'react-date-range';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import { multiSelectStyle } from '../../../../constants/multiSelectDropdown/multiSelectStyle';
import { ReactComponent as DoneLogo } from '../../../../images/logo-done.svg';
import ClearIcon from '@mui/icons-material/Clear';
import { IoSearchSharp } from 'react-icons/io5';
import BackspaceIcon from '@mui/icons-material/Backspace';
import CancelIcon from '@mui/icons-material/Cancel';
import InfiniteScroll from 'react-infinite-scroll-component';
import Table from '../../../../components/shared/Table';
import moment from 'moment';
import { handleCampaignReportStatusesBadge } from '../../../../constants/extras/statusesBadge';
import {
  dataExport,
  xlsxDataExport
} from '../../../../constants/extras/dataExport';
import {
  CampaignReportHeaders,
  formatBCampaignReportTransactions
} from '../../../../constants/csv/csvCampaignReport';

const pageLimit = 100;

export default function BonusReport() {
  const [getReport, { isFetching }] = useLazyGetCampaignReportQuery();
  const { data: camps } = useGetBonusCampaignQuery();
  const CampaignList = useMemo(() => camps?.map(c => ({ label: c?.name, value: c?._id })) || [], [camps]);

  const isRefetch = useSelector((state) => state.refetch.value);
  const { data: metros } = useGetMetrosQuery(null, {
    refetchOnMountOrArgChange: true
  });

  const [reportData, setReportData] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [metro, setMetro] = useState([]);
  const [status, setStatus] = useState('');
  const [campaign, setCampaign] = useState('');
  const [type, setType] = useState('');
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [fromDate, setFromDate] = useState(
    new DateCreater(new Date(new Date().setDate(new Date().getDate() - 14))).dt
  );
  const [toDate, setToDate] = useState(new DateCreater(new Date()).dt);
  const [state, setState] = useState([
    {
      startDate: new Date(new Date().setDate(new Date().getDate() - 14)),
      endDate: new Date(),
      key: 'selection'
    }
  ]);
  const [prevDate, setPrevDate] = useState(state);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [csvData, setCsvData] = useState(false);
  const [loadingCsv, setLoading] = useState(false);

  const wrapperRef = useRef(null);
  const csvInstance = useRef();

  const fetchReport = async (param = {}) => {
    const { refetch, clearSearch } = param;

    const result = await getReport({
      page: refetch ? 1 : page,
      limit: pageLimit,
      search: clearSearch ? '' : searchQuery,
      status: status?.value || '',
      type: type?.value || '',
      campaign: campaign?.value || '',
      fromDate,
      toDate,
      metros: JSON.stringify(metro?.map((item) => item.value))
    }).unwrap();

    if (refetch) setReportData(result.data);
    else setReportData((prev) => [...prev, ...result.data]);

    setCount(result.count);
    setHasMore(result.data.length === pageLimit);
  };

  useEffect(() => {
    fetchReport();
  }, [page]);

  useEffect(() => {
    if (!isRefetch) return;
    handleFilter();
    actions.refetch.setIsDataRefetch(false);
  }, [isRefetch]);

  const handleFilter = (params = {}) => {
    const { clearSearch } = params;
    setReportData([]);
    setCount(0);
    setHasMore(true);

    fetchReport({ refetch: true, clearSearch });
  };

  const onCancelClick = () => {
    setShowDateRangePicker(false);
    setState(prevDate);
  };

  const onResetClick = async () => {
    setShowDateRangePicker(false);
    const currDt = [
      {
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection'
      }
    ];
    const { dt: strtDt } = new DateCreater(new Date());
    const { dt: endDt } = new DateCreater(new Date());
    setFromDate(strtDt);
    setToDate(endDt);
    setState(currDt);
    setPrevDate(currDt);
    actions.refetch.setIsDataRefetch(true);
  };

  const handleTimeRangeSelection = async () => {
    const { startDate, endDate } = state[0] || {};
    const { dt: strtDt } = new DateCreater(startDate);
    const { dt: endDt } = new DateCreater(endDate);
    setFromDate(strtDt);
    setToDate(endDt);
    setShowDateRangePicker(false);
    setPrevDate(state);
    actions.refetch.setIsDataRefetch(true);
  };

  const exportData = async (value) => {
    setAnchorEl(null);
    setLoading(true);

    const result = await getReport({
      search: searchQuery,
      status: status?.value || '',
      type: type?.value || '',
      campaign: campaign?.value || '',
      fromDate,
      toDate,
      metros: JSON.stringify(metro?.map((item) => item.value))
    }).unwrap();

    setLoading(false);

    const filename = 'CampaignReport.csv';
    const data = formatBCampaignReportTransactions(result?.data || []);
    if (value === 'csv') {
      const obj = dataExport(data, filename, CampaignReportHeaders);
      setCsvData(obj);
    } else if (value === 'xlsx') {
      xlsxDataExport(data, 'CampaignReport');
    }
  };

  useEffect(() => {
    if (csvData && csvInstance.current && csvInstance.current.link) {
      setTimeout(() => {
        csvInstance.current.link.click();
        setCsvData(false);
      });
    }
  }, [csvData]);

  const columns = useMemo(
    () => [
      {
        name: 'name',
        label: 'Driver Name',
        sortable: true
      },
      {
        name: 'username',
        label: 'Username',
        sortable: true
      },
      {
        name: 'metro',
        label: 'Metro',
        sortable: true
      },
      {
        name: 'referredBy',
        label: 'Referred By',
        sortable: true,
        renderer: ({ referredBy }) => referredBy ? `${referredBy?.name} (${referredBy?.username})` : '-'
      },
      {
        name: 'campaign.name',
        label: 'Campaign',
        sortable: true,
        renderer: ({ campaign }) => campaign?.name
      },
      {
        name: 'campaign.type',
        label: 'Type',
        sortable: true,
        renderer: ({ campaign }) => campaign?.type
      },
      {
        name: 'routeCompleted',
        label: 'Routes',
        sortable: true
      },
      {
        name: 'remainingDays',
        label: 'Remaining Days',
        sortable: true,
        renderer: (data) => {
          console.log(data , "dat6a ")
          return data.bonusStatus === "In Progress" ? ((data?.remainingDays == 0) ? '0' : data?.remainingDays) : '-'}
      },

      {
        name: 'date',
        label: 'Date',
        sortable: true,
        renderer: ({ date }) => moment(date).format('MM-DD-YYYY hh:mm A')
      },
      {
        name: 'bonusStatus',
        label: 'Status',
        sortable: true,
        renderer: ({ bonusStatus }) => (
          <div style={{ padding: '8px 0' }}>
            <span style={handleCampaignReportStatusesBadge(bonusStatus)}>
              {bonusStatus}
            </span>
          </div>
        )
      }
    ],
    [reportData]
  );

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div className="card">
            <div
              style={{
                display: 'flex',
                padding: '10px',
                zIndex: 999
              }}
            >
              <div
                style={{
                  width: '350px',
                  position: 'relative'
                }}
              >
                <div style={{ width: '100%', padding: '20px' }}>
                  <h3
                    className={`card-title header__text ${
                      reportData?.length <= 0 || reportData?.length === '0'
                        ? 'mt-2'
                        : ''
                    }`}
                  >
                    Report List
                  </h3>
                  <p
                    style={{
                      position: 'absolute',
                      top: '45px',
                      color: '#4d4d4d',
                      fontSize: '0.8rem',
                      fontWeight: 600
                    }}
                  >
                    {reportData?.length > 0 &&
                      (reportData?.length < 10
                        ? '0'.concat(
                            `${reportData?.length} Report on Screen (${count} Found)`
                          )
                        : `${reportData?.length} Report on Screen (${count} Found)`)}
                  </p>
                </div>
              </div>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'end'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexWrap: 'wrap',
                    alignItems: 'center'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      flexWrap: 'wrap',
                      justifyContent: 'end',
                      width: '100%'
                    }}
                  >
                    <ReactSelect
                      options={CampaignList}
                      isClearable={true}
                      onChange={(item) => {
                        setCampaign(item);
                        actions.refetch.setIsDataRefetch(true);
                      }}
                      placeholder="Select Campaign"
                      value={campaign}
                      components={{
                        IndicatorSeparator: () => null
                      }}
                      styles={{
                        placeholder: (base) => ({
                          ...base,
                          fontSize: '13px',
                          fontWeight: 'normal'
                        }),
                        control: (styles) => ({
                          ...styles,
                          borderRadius: '5px',
                          boxShadow: 'none',
                          height: '2.8rem',
                          '&:hover': {
                            border: '2px solid #dadada'
                          },
                          border: '2px solid #dadada',
                          fontSize: '13px',
                          width: '185px'
                        })
                      }}
                    />

                    <ReactSelect
                      options={CampaignType?.map((item) => ({
                        label: item,
                        value: item
                      }))}
                      isClearable={true}
                      onChange={(item) => {
                        setType(item);
                        actions.refetch.setIsDataRefetch(true);
                      }}
                      placeholder="Select Type"
                      value={type}
                      components={{
                        IndicatorSeparator: () => null
                      }}
                      styles={{
                        placeholder: (base) => ({
                          ...base,
                          fontSize: '13px',
                          fontWeight: 'normal'
                        }),
                        control: (styles) => ({
                          ...styles,
                          borderRadius: '5px',
                          boxShadow: 'none',
                          height: '2.8rem',
                          '&:hover': {
                            border: '2px solid #dadada'
                          },
                          border: '2px solid #dadada',
                          fontSize: '13px',
                          width: '185px'
                        })
                      }}
                    />

                    <ReactSelect
                      options={CampaignReportStatus?.map((item) => {
                        return {
                          value: item,
                          label: item
                        };
                      })}
                      isClearable={true}
                      onChange={(item) => {
                        setStatus(item);
                        actions.refetch.setIsDataRefetch(true);
                      }}
                      placeholder="Select Status"
                      value={status}
                      components={{
                        IndicatorSeparator: () => null
                      }}
                      styles={{
                        placeholder: (base) => ({
                          ...base,
                          fontSize: '13px',
                          fontWeight: 'normal'
                        }),
                        control: (styles) => ({
                          ...styles,
                          borderRadius: '5px',
                          boxShadow: 'none',
                          height: '2.8rem',
                          '&:hover': {
                            border: '2px solid #dadada'
                          },
                          border: '2px solid #dadada',
                          fontSize: '13px',
                          width: '185px'
                        })
                      }}
                    />

                    <div className="d-flex justify-content-end status_filter">
                      <MultiSelect
                        placeholder="Select Metros"
                        value={metro}
                        itemData={metros?.map((item, i) => ({
                          value: item?.name ? item?.name : '-',
                          label: `${item?.name ? item?.name : ''}`
                        }))}
                        styles={{
                          ...multiSelectStyle(),
                          control: (styles) => ({
                            ...styles,
                            borderRadius: '5px',
                            boxShadow: 'none',
                            '&:hover': {
                              borderColor: '#dadada'
                            },
                            border: '2px solid #dadada !important',
                            borderRight: 'none !important',
                            borderTopRightRadius: '0 !important',
                            borderBottomRightRadius: '0 !important',
                            width: '200px',
                            height: '2.8rem',
                            overflowY: 'auto',
                            padding: 0
                          }),
                          menu: (styles) => ({
                            ...styles,
                            width: '250px'
                          })
                        }}
                        onChange={(value) => {
                          setMetro(value);
                          if (!value?.length)
                            actions.refetch.setIsDataRefetch(true);
                        }}
                      />
                      <button
                        disabled={metro?.length < 1}
                        className="search__button"
                        onClick={() => {
                          handleFilter();
                        }}
                      >
                        <DoneLogo fill="#5fb6f7" width="24px" height="24px" />
                      </button>
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      flexWrap: 'wrap',
                      justifyContent: 'end',
                      width: '100%',
                      marginTop: '9px'
                    }}
                  >
                    <div className="filter__ctrls-holder filter__input-holder d-flex justify-content-end search__container">
                      <div className="searchbar__container">
                        <input
                          className="form__input customized__input"
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            if (e.target.value === '') {
                              setSearchQuery('');
                              handleFilter({ clearSearch: true });
                            }
                          }}
                          placeholder="Quick Search"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleFilter();
                            }
                          }}
                          disabled={isFetching}
                        />
                        {searchQuery?.length > 0 && (
                          <ClearIcon
                            className="clear__btn filter__search-icon"
                            onClick={() => {
                              if (!isFetching && searchQuery?.length > 0) {
                                setSearchQuery('');
                                handleFilter({ clearSearch: true });
                              }
                            }}
                            style={{
                              right: '45px',
                              top: '12px'
                            }}
                          />
                        )}
                        <button
                          disabled={searchQuery?.length <= 0}
                          className="search__button"
                          onClick={() => handleFilter()}
                        >
                          <IoSearchSharp />
                        </button>
                      </div>
                    </div>

                    <div ref={wrapperRef} style={{ position: 'relative' }}>
                      <div
                        onClick={() => {
                          !isFetching &&
                            setShowDateRangePicker((prev) => !prev);
                          setState(prevDate);
                        }}
                        className="form__input "
                      >
                        <span>
                          {fromDate
                            ? `${createDate(
                                fromDate,
                                DATE.M_D_Y
                              )} TO ${createDate(toDate, DATE.M_D_Y)}`
                            : 'Select Custom Date Range'}
                        </span>
                        <span className="ml-2">
                          {showDateRangePicker ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </span>
                      </div>
                      {showDateRangePicker && (
                        <div
                          className="mainDateRangePicker mt-3"
                          style={{
                            position: 'absolute',
                            top: '45px',
                            right: '-100px'
                          }}
                        >
                          <DateRangePicker
                            onChange={(item) => setState([item.selection])}
                            months={1}
                            ranges={state}
                          />
                          <div className="button-container">
                            <button
                              onClick={onResetClick}
                              className="okButton clearButton"
                            >
                              <span>
                                <BackspaceIcon /> &nbsp;
                                <strong>RESET</strong>
                              </span>
                            </button>
                            <button
                              onClick={onCancelClick}
                              className="cancelButton"
                            >
                              <span>
                                <CancelIcon
                                  sx={{ width: '1.5rem', height: '1.5rem' }}
                                />
                                <strong>cancel</strong>
                              </span>
                            </button>
                            <button
                              onClick={handleTimeRangeSelection}
                              className="okButton"
                            >
                              <span>
                                <ThumbUpAltIcon /> &nbsp;
                                <strong>Done</strong>
                              </span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <button
                        id="fade-button"
                        className="btn btn_block btn_accent btn_export_15"
                        onClick={(event) => setAnchorEl(event.currentTarget)}
                        variant="contained"
                        disabled={
                          loadingCsv || reportData?.length === 0 ? true : false
                        }
                      >
                        {loadingCsv ? (
                          <div className="loading" style={{ color: 'white' }}>
                            <span
                              className="loading__text"
                              style={{ color: 'white' }}
                            >
                              Exporting
                            </span>
                            ...
                          </div>
                        ) : (
                          <span>
                            Export <KeyboardArrowDownIcon className="mr-0" />
                          </span>
                        )}
                      </button>
                      <Menu
                        id="fade-menu"
                        MenuListProps={{
                          'aria-labelledby': 'fade-button'
                        }}
                        anchorEl={anchorEl}
                        open={open}
                        onClick={() => setAnchorEl(null)}
                        TransitionComponent={Fade}
                      >
                        {exportMenu.map(({ value, name }) => (
                          <MenuItem
                            key={name}
                            value={name}
                            onClick={() => exportData(value)}
                            style={{ width: '100px' }}
                          >
                            {name}
                          </MenuItem>
                        ))}
                      </Menu>
                    </div>

                    {csvData ? (
                      <CSVLink
                        data={csvData.data}
                        headers={csvData.headers}
                        filename={csvData.filename}
                        ref={csvInstance}
                      />
                    ) : undefined}

                    <button
                      className="refresh_btn"
                      onClick={() => actions.refetch.setIsDataRefetch(true)}
                    >
                      <Refresh className="refresh_icon" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <InfiniteScroll
              className="infinit_table"
              dataLength={reportData?.length}
              next={() => {
                if (!isFetching && hasMore) {
                  setPage((prev) => prev + 1);
                }
              }}
              hasMore={hasMore}
              style={{
                paddingBottom: !hasMore ? '0px' : '10px',
                borderTop: '1px solid #dadada'
              }}
              loader={
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{
                    height:
                      reportData && reportData?.length <= 0 ? '70vh' : 'auto'
                  }}
                >
                  <CircularProgress className="loading-progress" />
                </div>
              }
              height={'75vh'}
              endMessage={
                reportData &&
                reportData?.length <= 0 && (
                  <div
                    style={{
                      height: '70vh'
                    }}
                    className="d-flex justify-content-center align-items-center"
                  >
                    <p>
                      <b>No Result Found</b>
                    </p>
                  </div>
                )
              }
            >
              {isFetching && !reportData.length ? (
                <div className="loading-holder">
                  <CircularProgress className="loading-progress" />
                </div>
              ) : (
                <Table
                  columns={columns}
                  items={reportData}
                  className="table_drivers"
                  width={'100%'}
                />
              )}
            </InfiniteScroll>
          </div>
        </div>
      </div>
    </div>
  );
}
